import { useState, type ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

export interface InvitationDetails {
  email: string;
  role: string;
  tenant_name: string;
  inviter_name: string;
}

export interface AcceptInvitationFormProps {
  invitationDetails: InvitationDetails;
  onSignUp: (password: string, confirmPassword: string) => void;
  onLogin: (password: string) => void;
  onMagicLink: () => void;
  loading?: boolean;
  error?: string | null;
  validationError?: string | null;
  /**
   * Optional captcha widget. The parent owns the Turnstile lifecycle
   * via `useTurnstile()` and passes the rendered `<div ref={...} />`
   * here; we mount it inside both tab panels so it's visible whichever
   * auth path the user chooses.
   */
  captchaSlot?: ReactNode;
  /**
   * Force-disable the three submit buttons (Accept, Log In & Accept,
   * Use Magic Link). The parent sets this true when a captcha token
   * is required but not yet present.
   */
  submitDisabled?: boolean;
}

export function AcceptInvitationForm({
  invitationDetails,
  onSignUp,
  onLogin,
  onMagicLink,
  loading = false,
  error = null,
  validationError = null,
  captchaSlot,
  submitDisabled = false,
}: AcceptInvitationFormProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSignUpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSignUp(password, confirmPassword);
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onLogin(password);
  };

  return (
    <div className="w-full max-w-md">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>You're Invited!</CardTitle>
          <CardDescription>
            {invitationDetails.inviter_name} invited you to join{" "}
            <strong>{invitationDetails.tenant_name}</strong> as{" "}
            <Badge variant="outline">{invitationDetails.role}</Badge>
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue="signup" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="signup">Sign Up</TabsTrigger>
          <TabsTrigger value="login">Log In</TabsTrigger>
        </TabsList>

        <TabsContent value="signup">
          <Card>
            <CardHeader>
              <CardTitle>Create Account</CardTitle>
              <CardDescription>
                Create a new account to accept this invitation
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSignUpSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                {validationError && (
                  <Alert variant="destructive">
                    <AlertDescription>{validationError}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={invitationDetails.email}
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    disabled={loading}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">
                    Confirm Password
                  </Label>
                  <Input
                    id="signup-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    disabled={loading}
                    required
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || submitDisabled}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Accept Invitation
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={onMagicLink}
                  disabled={loading || submitDisabled}
                >
                  Use Magic Link Instead
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>

        <TabsContent value="login">
          <Card>
            <CardHeader>
              <CardTitle>Log In</CardTitle>
              <CardDescription>
                Log in with your existing account to accept this invitation
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleLoginSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={invitationDetails.email}
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    disabled={loading}
                    required
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || submitDisabled}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Log In & Accept
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>
      </Tabs>

      {captchaSlot && <div className="mt-4">{captchaSlot}</div>}
    </div>
  );
}
